import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { catsApi, feedingRequestsApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';
import CatCard from '../components/CatCard';

const UserDashboard = () => {
  const [stats, setStats] = useState({
    requestedFeedings: 0,
    completedFeedings: 0,
    availableCats: 0
  });
  const [recentCats, setRecentCats] = useState([]);
  const [feedingRequests, setFeedingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch feeding stats from the new endpoint
        const feedingStats = await feedingRequestsApi.getFeedingStats();
        
        // Fetch feeding requests - with better handling of response format
        const requestsData = await feedingRequestsApi.getUserFeedingRequests(0, 5); // First page, 5 items
        console.log("Feeding requests response:", requestsData);
        
        // Handle different API response formats
        const requests = Array.isArray(requestsData) 
          ? requestsData 
          : (requestsData.data || []);
          
        console.log("Processed feeding requests:", requests);
        setFeedingRequests(requests);
        
        // Update stats using the new feedingStats data
        setStats({
          requestedFeedings: feedingStats.openFeedingRequestsCount || 0,
          completedFeedings: feedingStats.completedFeedingRequestsCount || 0,
          availableCats: feedingStats.recentCats?.length || 0
        });
        
        // Set recent cats from the new endpoint
        setRecentCats(feedingStats.recentCats || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // Handle feed request success
  const handleFeedRequestSuccess = (catId) => {
    // Update stats
    setStats(prev => ({
      ...prev,
      requestedFeedings: prev.requestedFeedings + 1
    }));
    
    // Update cat in recent cats list if it exists
    setRecentCats(prevCats => 
      prevCats.map(cat => 
        cat.id === catId 
          ? { ...cat, pendingRequests: (cat.pendingRequests || 0) + 1 } 
          : cat
      )
    );
  };
  
  return (
    <div className="user-dashboard">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-description">
          Welcome to your WellFed Cats dashboard!
        </p>
      </div>
      
      {/* Stats section */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon">🍽️</div>
          <h3 className="stat-value">{stats.requestedFeedings}</h3>
          <p className="stat-label">Requested Feedings</p>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <h3 className="stat-value">{stats.completedFeedings}</h3>
          <p className="stat-label">Completed Feedings</p>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🐱</div>
          <h3 className="stat-value">{stats.availableCats}</h3>
          <p className="stat-label">Recent Cats</p>
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button className="button" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      )}
      
      {!loading && !error && (
        <>
          {/* Recent feeding requests section */}
          <div className="section-divider">
            <h2>Recent Feeding Requests</h2>
            <div className="divider-line"></div>
          </div>
          
          {feedingRequests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🍽️</div>
              <h2 className="empty-state-title">No feeding requests yet</h2>
              <p className="empty-state-description">
                You haven't requested any cat feedings yet. Browse our cats and make your first request!
              </p>
              <Link to="/dashboard/cats" className="button">
                Browse Cats
              </Link>
            </div>
          ) : (
            <div className="requests-list">
              {feedingRequests.map(request => {
                // Handle both the old and new format
                const cat = request.cat || {};
                return (
                  <div key={request.id} className="request-item">
                    <div className="request-cat-info">
                      <img 
                        src={(cat.catPhotos && cat.catPhotos.length > 0)
                          ? cat.catPhotos[0].path
                          : request.catPhoto || '/cat-placeholder.png'} 
                        alt={cat.name || request.catName || 'Cat'} 
                        className="request-cat-image"
                        onError={(e) => {
                          e.target.src = '/cat-placeholder.png';
                        }}
                      />
                      <div className="request-details">
                        <h3>{cat.name || request.catName || 'Unknown Cat'}</h3>
                        <p className="request-date">
                          Requested on {request.createdAt 
                            ? new Date(request.createdAt).toLocaleDateString() 
                            : 'Unknown date'}
                        </p>
                        <p className="request-status">
                          Status: <span className={request.isProcessed ? 'status-completed' : 'status-pending'}>
                            {request.isProcessed ? 'Completed' : 'Pending'}
                          </span>
                        </p>
                        
                        {/* Display feeding notes for completed requests */}
                        {request.isProcessed && request.feeding && request.feeding.notes && (
                          <p className="request-notes">
                            <span className="notes-label">Feeder Notes:</span> {request.feeding.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="request-actions">
                      <Link 
                        to={request.isProcessed 
                          ? `/dashboard/feeding-request/processed` 
                          : `/dashboard/feeding-request/open`} 
                        className="button view-button"
                      >
                        View All Requests
                      </Link>
                    </div>
                  </div>
                );
              })}
              
              <div className="view-all-link">
                <Link to="/dashboard/feeding-request/open">
                  View All Requests
                </Link>
              </div>
            </div>
          )}
          
          {/* Recent cats section */}
          <div className="section-divider">
            <h2>Recent Cats</h2>
            <div className="divider-line"></div>
          </div>
          
          {recentCats.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🐱</div>
              <h2 className="empty-state-title">No cats available</h2>
              <p className="empty-state-description">
                There are no cats available at the moment. Please check back later.
              </p>
            </div>
          ) : (
            <>
              <div className="cards-grid">
                {recentCats.map(cat => (
                  <CatCard 
                    key={cat.id} 
                    cat={cat} 
                    onRequestSuccess={handleFeedRequestSuccess}
                  />
                ))}
              </div>
              
              <div className="view-all-link">
                <Link to="/dashboard/cats" className="button">
                  View All Cats
                </Link>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default UserDashboard; 